import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { fetchUsers } from './api';
import type { AdminUser, UsersMeta } from './types';
import { compactSelectClass, secondaryBtnClass } from '../styles';

interface UserPickerListProps {
  meta: UsersMeta;
  mode: 'single' | 'multi';
  selectedIds: number[];
  onChange: (ids: number[], users: AdminUser[]) => void;
  refreshToken?: number;
}

const UserPickerList: React.FC<UserPickerListProps> = ({
  meta,
  mode,
  selectedIds,
  onChange,
  refreshToken = 0,
}) => {
  const [q, setSearch] = useState('');
  const [org, setOrg] = useState<number | ''>('');
  const [userType, setUserType] = useState<string>('');
  const [isActive, setIsActive] = useState<'' | 'true' | 'false'>('true');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminUser[]>([]);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const allowedTypes = meta.user_types.filter((t) =>
    t.key === 'catalyx' ? meta.permissions.edit_catalyx : meta.permissions.edit_client
  );

  const load = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchUsers({
        q: q.trim(),
        org,
        user_type: userType,
        is_active: isActive,
        page: nextPage,
      });
      setData(payload.results);
      setCount(payload.count);
      setPage(payload.page);
      setPageSize(payload.page_size);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshToken) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const selectedSet = new Set(selectedIds);
  const visibleSelected = data.filter((u) => selectedSet.has(u.id));
  const allVisibleSelected = data.length > 0 && visibleSelected.length === data.length;

  const toggle = (user: AdminUser) => {
    if (mode === 'single') {
      onChange([user.id], [user]);
      return;
    }
    const next = selectedSet.has(user.id)
      ? selectedIds.filter((id) => id !== user.id)
      : [...selectedIds, user.id];
    const retained = data.filter((u) => next.includes(u.id));
    onChange(next, retained);
  };

  const togglePage = () => {
    if (mode !== 'multi') return;
    if (allVisibleSelected) {
      const remaining = selectedIds.filter((id) => !data.some((u) => u.id === id));
      onChange(remaining, []);
    } else {
      const merged = Array.from(new Set([...selectedIds, ...data.map((u) => u.id)]));
      onChange(merged, data);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
          <input
            value={q}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(1);
            }}
            placeholder="Search name, email, or company"
            className="h-10 w-full rounded-md border border-[#D1D1D1] pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#3da3d4]"
          />
        </div>
        <select
          value={org}
          onChange={(e) => setOrg(e.target.value ? Number(e.target.value) : '')}
          className={compactSelectClass}
          aria-label="Filter by organization"
        >
          <option value="">All organizations</option>
          {meta.organizations.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select
          value={userType}
          onChange={(e) => setUserType(e.target.value)}
          className={compactSelectClass}
          aria-label="Filter by user type"
        >
          <option value="">All user types</option>
          {allowedTypes.map((t) => (
            <option key={t.id} value={t.key}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          value={isActive}
          onChange={(e) => setIsActive(e.target.value as '' | 'true' | 'false')}
          className={compactSelectClass}
          aria-label="Filter by status"
        >
          <option value="">Active and deactivated</option>
          <option value="true">Active</option>
          <option value="false">Deactivated</option>
        </select>
        <button type="button" onClick={() => void load(1)} className={secondaryBtnClass}>
          Search
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  {mode === 'multi' && (
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={togglePage}
                      aria-label="Select all on this page"
                    />
                  )}
                </th>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Email</th>
                <th className="px-3 py-2.5 font-semibold">Organization</th>
                <th className="px-3 py-2.5 font-semibold">User type</th>
                <th className="px-3 py-2.5 font-semibold">Role</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((user) => {
                const selected = selectedSet.has(user.id);
                return (
                  <tr
                    key={user.id}
                    className={`border-t border-[#E8E8E8] cursor-pointer ${
                      selected ? 'bg-[#F3F8FC]' : 'hover:bg-[#FAFAFA]'
                    }`}
                    onClick={() => toggle(user)}
                  >
                    <td className="px-3 py-2.5">
                      {mode === 'multi' ? (
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggle(user)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <input
                          type="radio"
                          checked={selected}
                          onChange={() => toggle(user)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[#0C0B0B]">{user.name}</td>
                    <td className="px-3 py-2.5 text-[#4A4A4A]">{user.email}</td>
                    <td className="px-3 py-2.5 text-[#4A4A4A]">{user.organization_name || '—'}</td>
                    <td className="px-3 py-2.5">{user.user_type_name || '—'}</td>
                    <td className="px-3 py-2.5 text-[#4A4A4A]">
                      {user.user_type_key === 'catalyx'
                        ? user.catalyx_role_name || '—'
                        : user.client_role_name || '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      {user.is_active ? (
                        <span className="text-emerald-700">Active</span>
                      ) : (
                        <span className="text-[#8A8A8A]">Deactivated</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-[#8A8A8A]">
                    No users match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[12px] text-[#626262]">
        <span>
          {count} user{count === 1 ? '' : 's'}
          {mode === 'multi' && selectedIds.length > 0 ? ` · ${selectedIds.length} selected` : ''}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page <= 1 || loading}
            onClick={() => void load(page - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="text-[#3da3d4] disabled:opacity-30"
            disabled={page >= totalPages || loading}
            onClick={() => void load(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPickerList;
